import { useEffect, useRef, memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn, formatDate } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Calendar, MessageSquare, Paperclip, Clock, GripVertical, Trash2, Repeat, Play, Square } from 'lucide-react';
import { useTimerStore } from '@/stores/timer-store';
import { toast } from '@/hooks/use-toast';
import { useCreateTimeEntry } from '@/hooks/use-time-entries';

interface TaskCardProps {
  task: any;
  onClick?: () => void;
  onDelete?: (task: any) => void;
  highlighted?: boolean;
}

const priorityColors: Record<string, string> = {
  urgent: 'bg-red-500/10 text-red-400 border-red-500/20',
  high: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
  medium: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  low: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

export const TaskCard = memo(function TaskCard({ task, onClick, onDelete, highlighted }: TaskCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  // Scroll into view when highlighted
  useEffect(() => {
    if (highlighted && cardRef.current) {
      // Small delay to ensure the kanban board has rendered
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [highlighted]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  // Timer store — read-only for display, actions for buttons
  const isRunning = useTimerStore((s) => s.isRunning);
  const timerTaskId = useTimerStore((s) => s.taskId);
  const startTimer = useTimerStore((s) => s.startTimer);
  const stopTimer = useTimerStore((s) => s.stopTimer);
  const createEntry = useCreateTimeEntry();

  const isTimingThisTask = isRunning && timerTaskId === task.id;

  const handleToggleTimer = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (isTimingThisTask) {
      // Stop the running timer and create the time entry
      const result = stopTimer();
      if (!result) return;
      try {
        await createEntry.mutateAsync({
          taskId: result.taskId,
          hours: result.hours,
          date: new Date().toISOString().split('T')[0],
          description: `Timed entry — ${result.hours.toFixed(2)}h`,
          billable: true,
        });
        toast({ title: 'Time logged', description: `${result.hours.toFixed(2)}h saved from timer.`, variant: 'success' });
      } catch (err: any) {
        toast({ title: 'Failed to log time', description: err?.message, variant: 'destructive' });
      }
    } else {
      // Only start if no other timer is running (prevent silent discard)
      if (isRunning && timerTaskId !== task.id) {
        toast({
          title: 'Timer already running',
          description: 'Stop the current timer before starting a new one.',
          variant: 'destructive',
        });
        return;
      }
      startTimer(task.id, task.title, task.project?.name);
    }
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={(node) => {
        setNodeRef(node);
        (cardRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      }}
      style={style}
      className={cn(
        'group bg-[var(--card-bg)] border border-[var(--card-border)] rounded-lg p-3 cursor-pointer',
        'hover:border-dyndev-green/30 hover:shadow-md transition-all duration-200',
        isDragging && 'opacity-50 shadow-lg ring-2 ring-dyndev-green/50',
        highlighted && 'ring-2 ring-dyndev-green/70 shadow-lg shadow-dyndev-green/10 border-dyndev-green/50',
        'active:cursor-grabbing'
      )}
      onClick={onClick}
      {...attributes}
    >
      {/* Drag handle + title row */}
      <div className="flex items-start gap-2">
        <button
          className="mt-0.5 text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 transition-opacity cursor-grab flex-shrink-0"
          {...listeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={14} />
        </button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-[var(--text-primary)] line-clamp-2 leading-snug">
            {task.title}
          </p>
        </div>

        {/* Delete button */}
        {onDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(task);
            }}
            className="mt-0.5 text-[var(--text-secondary)] opacity-0 group-hover:opacity-100 hover:text-red-400 transition-all duration-200 flex-shrink-0"
            title="Delete task"
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      {/* Priority & Status badges */}
      <div className="flex flex-wrap gap-1.5 mt-2">
        {task.priority && (
          <Badge variant="outline" className={cn('text-[10px] px-1.5 py-0', priorityColors[task.priority])}>
            {task.priority}
          </Badge>
        )}
        {task.estimatedHours && (
          <span className="text-[10px] text-[var(--text-secondary)] flex items-center gap-0.5">
            <Clock size={10} />
            {task.estimatedHours}h
          </span>
        )}
      </div>

      {/* Footer: assignee, date, meta */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t border-[var(--border-color)]">
        <div className="flex items-center gap-2">
          {task.assignee ? (
            <Avatar className="w-5 h-5">
              <AvatarFallback className="text-[8px] bg-dyndev-green/20 text-dyndev-green">
                {task.assignee.name?.charAt(0)?.toUpperCase() || '?'}
              </AvatarFallback>
            </Avatar>
          ) : (
            <div className="w-5 h-5 rounded-full bg-[var(--bg-secondary)] border border-[var(--border-color)]" />
          )}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-[var(--text-secondary)]">
          {/* Timer button */}
          <button
            onClick={handleToggleTimer}
            className={cn(
              'flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors',
              isTimingThisTask
                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                : 'bg-dyndev-green/10 text-dyndev-green hover:bg-dyndev-green/20 opacity-0 group-hover:opacity-100'
            )}
            title={isTimingThisTask ? 'Stop timer' : 'Start timer'}
          >
            {isTimingThisTask ? <Square size={10} /> : <Play size={10} />}
            {isTimingThisTask && 'Stop'}
            {!isRunning && !isTimingThisTask && 'Start'}
          </button>
          {task.dueDate && (
            <span className="flex items-center gap-0.5">
              <Calendar size={10} />
              {formatDate(task.dueDate)}
            </span>
          )}
          {task._count?.comments > 0 && (
            <span className="flex items-center gap-0.5">
              <MessageSquare size={10} />
              {task._count.comments}
            </span>
          )}
          {task._count?.subtasks > 0 && (
            <span className="flex items-center gap-0.5">
              <Paperclip size={10} />
              {task._count.subtasks}
            </span>
          )}
          {task.isRecurring && (
            <span className="flex items-center gap-0.5 text-dyndev-green" title={task.recurringRule ? `Every ${task.recurringRule.interval || 1} ${task.recurringRule.freq}(s)` : 'Recurring'}>
              <Repeat size={10} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
});
